import {
  ASSESSMENT_MODULE_SEED,
  MODULE_PRESENTATION,
  NFR_TARGETS,
  type AssessmentModuleCode,
  type DetectionState,
  type ModuleChecklistItem,
  type SeverityTier,
} from "@taskiq/shared";

/**
 * The script the marketing site's replay plays back.
 *
 * The budget comes from `NFR_TARGETS`, the checklists and their labels from the
 * module seed the bootstrap writes into `assessment_modules.config`, and the
 * severity vocabulary from the shared constant. A hard-coded `90` on a marketing
 * page is a number that keeps claiming ninety after the product has moved, and a
 * hand-typed PPE list is one that keeps saying "Eye protection" after the
 * catalogue started saying "Safety glasses" - which is exactly what it had
 * drifted into.
 *
 * The one thing deliberately *not* derived is where the ninety seconds are
 * split; see [BEATS] for why a diagram wants round numbers.
 *
 * ── What the replay is showing, and what it is not ─────────────────────────
 *
 * Five beats, because the flow has five things a technician would name. Four of
 * them are modules (`area_scan` twice - the sweep and the list it returns -
 * then `heights`, then `ppe`) and the fifth is the sign-off. `utility` is off by
 * default in `MODULE_PRESENTATION`, so it is off here too.
 *
 * **`heights` is one beat with two steps, not two beats.** It is one module and
 * one `assessments` row, and it records two different kinds of fact: an
 * inclinometer reading, which is a measurement, and a set of photographs of the
 * set-up, which go to the same detector PPE's items do.
 * Splitting them across the scale would say they were two checks; collapsing
 * them to the angle alone - which is what this file used to do - loses the half
 * of the module that a model actually reads.
 */

export const BUDGET_SECONDS = NFR_TARGETS.assessmentSeconds;

/* ──────────────────────── what the catalogue says ──────────────────────── */

function seed(code: AssessmentModuleCode) {
  const row = ASSESSMENT_MODULE_SEED.find((module) => module.code === code);
  if (!row) throw new Error(`No seed for assessment module "${code}"`);
  return row;
}

/**
 * The seeded config, read straight rather than through a schema.
 *
 * The console reads the live catalogue from the API because an administrator can
 * edit it. A marketing page has no session and no tenant, so it reads what
 * `npm run bootstrap` wrote - which is the same thing for every field below,
 * none of which an administrator has a reason to change.
 */
const AREA_SCAN = seed("area_scan").config as { min_seconds: number; max_seconds: number };

const HEIGHTS = seed("heights").config as {
  target_angle_deg: number;
  tolerance_deg: number;
  auto_capture_band_deg: number;
  checks: ModuleChecklistItem[];
  attested: string[];
};

const PPE = seed("ppe").config as { items: ModuleChecklistItem[] };

/* ─────────────────────────────── the clock ─────────────────────────────── */

/**
 * Sign-off is the one beat with no module behind it: a confirmation, a location
 * fix and a write. Five seconds is what that costs and nothing in the catalogue
 * knows it.
 */
const SIGNOFF_SECONDS = 5;

/**
 * Where the ninety seconds go.
 *
 * ── Why these are written down rather than derived ────────────────────────
 *
 * They were derived, and it was the wrong call. The default module set targets
 * 45 + 40 + 15 seconds plus a sign-off, which is 105 against a 90-second
 * budget - `MODULE_PRESENTATION` says so in its own comment and calls the
 * resolution a product call. Scaling 105 onto 90 is arithmetically honest and
 * produces 26s, 13s, 34s, 13s, 4s: five numbers that look measured to the
 * second and are nothing of the kind, printed on a diagram whose whole job is
 * to be read at a glance.
 *
 * A landing page is not the record. It is allowed to say "half a minute" where
 * the record says 34.2 seconds, and a reader takes more away from 30 / 15 / 30 /
 * 10 / 5 than from any amount of spurious precision. So these are round, they
 * keep the product's shape - the sweep is the expensive part, heights is the
 * next one, sign-off is a rounding error - and the scan's two beats still sum to
 * `area_scan`'s real 45-second target.
 *
 * What they must do is sum to [BUDGET_SECONDS], which is the one number on this
 * page that is a commitment. [assertBudget] says so out loud in development
 * rather than throwing: this is the entry chunk, and a marketing site that fails
 * to load is worse than one whose hero is five seconds out.
 */
const BEATS = [
  { id: "scan", label: "Area scan", short: "Scan", seconds: 20 },
  { id: "acknowledge", label: "Review findings", short: "Review", seconds: 15 },
  { id: "heights", label: "Working at heights", short: "Heights", seconds: 30 },
  { id: "ppe", label: "PPE check", short: "PPE", seconds: 20 },
  { id: "signoff", label: "Sign off", short: "Sign off", seconds: 5 },
] as const;

export type PhaseId = (typeof BEATS)[number]["id"];

export interface Phase {
  id: PhaseId;
  label: string;
  /** One word where possible. The scale gives every phase the same width, so
   *  this is what has to fit in a fifth of the track on a phone. */
  short: string;
  from: number;
  to: number;
}

export const PHASES: Phase[] = (() => {
  let from = 0;
  return BEATS.map((beat) => {
    const phase = {
      id: beat.id,
      label: beat.label,
      short: beat.short,
      from,
      to: from + beat.seconds,
    };
    from = phase.to;
    return phase;
  });
})();

if (import.meta.env.DEV) {
  const total = PHASES[PHASES.length - 1]!.to;
  if (total !== BUDGET_SECONDS) {
    console.warn(
      `[replay] the beats sum to ${total}s against a ${BUDGET_SECONDS}s budget - the hero's clock and its headline no longer agree`
    );
  }
}

export function phaseAt(seconds: number): Phase {
  return PHASES.find((phase) => seconds < phase.to) ?? PHASES[PHASES.length - 1]!;
}

function phase(id: PhaseId): Phase {
  const found = PHASES.find((p) => p.id === id);
  if (!found) throw new Error(`No phase "${id}"`);
  return found;
}

/** `0:07`, for a clock a technician would recognise. */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/* ─────────────────────────────── the sweep ─────────────────────────────── */

export const SWEEP_SECONDS = {
  min: AREA_SCAN.min_seconds,
  max: AREA_SCAN.max_seconds,
} as const;

export interface Finding {
  severity: SeverityTier;
  text: string;
  /** The second it lands on the technician's screen. */
  at: number;
}

/**
 * The illustrative findings from PRD §6, phrased the way the product phrases
 * them - "wasp nest in the eave above the ladder position", not "object class 47
 * detected". A finding a technician cannot picture is one they will not act on.
 *
 * They land evenly across the review beat rather than at typed-in seconds, so
 * moving the beat moves them with it and none can fall off the end of it.
 */
export const FINDINGS: Finding[] = (
  [
    { severity: "stop", text: "Overhead service drop directly above the ladder position" },
    { severity: "caution", text: "Wasp nest in the eave, upper right" },
    { severity: "caution", text: "Loose rock along the approach path" },
    { severity: "advisory", text: "Vegetation narrowing egress on the north side" },
  ] satisfies Omit<Finding, "at">[]
).map((finding, index, all) => {
  const review = phase("acknowledge");
  return {
    ...finding,
    at: review.from + ((index + 1) / (all.length + 1)) * (review.to - review.from),
  };
});

/* ───────────────────────────── the detections ──────────────────────────── */

/**
 * One row the way a detector answers it, before a technician touches it.
 *
 * Deliberately the shape of `checklistDetectionSchema` rather than of a
 * checkbox: a code, one of three states, a band and the model's own sentence.
 * The state is the point. A checklist has two states and asserts the second one
 * whenever it cannot prove the first; "we did not look" and "it was not there"
 * are different claims, and `not_assessed` is the product's whole answer to why
 * it is not a checkbox app.
 */
export interface ScriptedDetection {
  code: string;
  label: string;
  detected: DetectionState;
}

/**
 * The verdict a row reads back as, in the console's own vocabulary
 * (`checkBasis` in `AssessmentPanel.tsx`). Never a percentage anywhere: the
 * figure is a language model's self-report, it is not calibrated, and printing
 * it invites an argument in a compliance review. **The number is storage, the
 * word is the record.**
 */
export function detectionVerdict(detection: ScriptedDetection): string {
  switch (detection.detected) {
    case "present":
      return "Found";
    case "absent":
      return "Not there";
    case "not_assessed":
      return "Not in shot";
  }
}

function script(
  items: readonly ModuleChecklistItem[],
  answers: Record<string, DetectionState>
): ScriptedDetection[] {
  return items.flatMap((item) => {
    const detected = answers[item.code];
    if (!detected) {
      if (import.meta.env.DEV) {
        console.warn(`[replay] no scripted detection for "${item.code}" - left out of the hero`);
      }
      return [];
    }
    return [{ code: item.code, label: item.label, detected }];
  });
}

/* ── Working at heights ──────────────────────────────────────────────────── */

export const HEIGHTS_ANGLE = {
  target: HEIGHTS.target_angle_deg,
  tolerance: HEIGHTS.tolerance_deg,
  band: HEIGHTS.auto_capture_band_deg,
  /** What the replay's device reads - inside tolerance, so the reading passes. */
  measured: 74.8,
  /** How long the phone must sit still before the reading takes itself. */
  holdSeconds: 5,
} as const;

/**
 * Where step 1 ends inside the heights beat.
 *
 * The reading is the shorter half: a five-second hold plus the settling either
 * side of it, against a handful of photographs and a round trip to a detector.
 */
export const HEIGHTS_STEP_SPLIT = 0.4;

/** The set-up checks a photograph can answer - everything `config.attested` is not. */
export const HEIGHTS_CHECKS: ScriptedDetection[] = script(
  HEIGHTS.checks.filter((check) => !HEIGHTS.attested.includes(check.code)),
  {
    base_level: "present",
    tie_off: "present",
    condition: "present",
    // The one that fails, and it is here on purpose. A demo where every row
    // comes back green is a demo of a checklist. The whole argument for a
    // detector is the row nobody would have ticked honestly at the eleventh
    // stop of the day.
    extension: "absent",
  }
);

/**
 * The one row nothing optical will ever answer.
 *
 * `config.attested` names it, the analyze endpoint takes it out of what it asks
 * the detector, and the phone renders it as a plain switch after the detector's
 * rows. It is on the landing page for the same reason it is in the config: a
 * product that says out loud what it refuses to guess at is one a safety
 * director can believe about the rest.
 */
export const HEIGHTS_ATTESTED: ModuleChecklistItem[] = HEIGHTS.checks.filter((check) =>
  HEIGHTS.attested.includes(check.code)
);

/* ── PPE ─────────────────────────────────────────────────────────────────── */

export const PPE_CHECKS: ScriptedDetection[] = script(PPE.items, {
  hard_hat: "present",
  safety_glasses: "present",
  gloves: "present",
  hi_vis: "present",
  // The third state, in the place it actually happens. An arm's-length selfie
  // stops above the knee, so a checklist would have had to assert boots here.
  boots: "not_assessed",
  harness: "present",
  lanyard: "present",
});
